import {MEMBERSHIP_POWER_LEVEL_EVENTS, RTC_PROTOCOL_V1} from '@phonehq/rtc-core';

export interface PowerLevelsContent {
    readonly events?: Readonly<Record<string, number>>;
    readonly [key: string]: unknown;
}

export interface RtcPowerLevelInput {
    readonly content: PowerLevelsContent;
    readonly userId: string;
    readonly maySendMembership: boolean;
    readonly maySendPowerLevels: boolean;
}

export type RtcPowerLevelPlan =
    | {readonly kind: 'ok'}
    | {readonly kind: 'heal'; readonly content: PowerLevelsContent; readonly addedEvents: readonly string[]}
    | {readonly kind: 'forbidden'; readonly reason: 'restricted' | 'no_permission'};

export type RtcPowerLevelOutcome = 'ok' | 'healed' | 'forbidden';

const numberOr = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

export const userPowerLevel = (content: PowerLevelsContent, userId: string): number => {
    const users = content['users'];
    const explicit = users && typeof users === 'object' ? (users as Record<string, unknown>)[userId] : undefined;
    return numberOr(explicit, numberOr(content['users_default'], 0));
};

export const planRtcPowerLevels = ({content, userId, maySendMembership, maySendPowerLevels}: RtcPowerLevelInput): RtcPowerLevelPlan => {
    if (maySendMembership) {
        return {kind: 'ok'};
    }
    const events = content.events ?? {};
    const membershipLevel = events[RTC_PROTOCOL_V1.membershipStateType];
    if (membershipLevel !== undefined) {
        return userPowerLevel(content, userId) >= membershipLevel
            ? {kind: 'ok'}
            : {kind: 'forbidden', reason: 'restricted'};
    }
    const missing = Object.keys(MEMBERSHIP_POWER_LEVEL_EVENTS).filter(eventType => events[eventType] === undefined);
    if (!maySendPowerLevels) {
        return {kind: 'forbidden', reason: 'no_permission'};
    }
    const additions = Object.fromEntries(
        missing.map(eventType => [eventType, MEMBERSHIP_POWER_LEVEL_EVENTS[eventType as keyof typeof MEMBERSHIP_POWER_LEVEL_EVENTS]]),
    );
    return {
        kind: 'heal',
        content: {...content, events: {...events, ...additions}},
        addedEvents: missing,
    };
};

export interface RtcPowerLevelRoomPort {
    maySendStateEvent(eventType: string, userId: string): boolean;
}

export interface RtcPowerLevelClientPort {
    getUserId(): string | null;
    getRoom(roomId: string): {readonly currentState: RtcPowerLevelRoomPort} | null;
    getStateEvent(roomId: string, eventType: string, stateKey: string): Promise<Record<string, unknown>>;
    sendStateEvent(roomId: string, eventType: string, content: Record<string, unknown>, stateKey: string): Promise<unknown>;
}

export const ensureRtcPowerLevels = async (
    client: RtcPowerLevelClientPort,
    roomId: string,
): Promise<RtcPowerLevelOutcome> => {
    const userId = client.getUserId();
    const room = client.getRoom(roomId);
    if (!userId || !room) {
        return 'forbidden';
    }
    if (room.currentState.maySendStateEvent(RTC_PROTOCOL_V1.membershipStateType, userId)) {
        return 'ok';
    }
    const latest = (await client.getStateEvent(roomId, 'm.room.power_levels', '')) as PowerLevelsContent;
    const plan = planRtcPowerLevels({
        content: latest ?? {},
        userId,
        maySendMembership: false,
        maySendPowerLevels: room.currentState.maySendStateEvent('m.room.power_levels', userId),
    });
    if (plan.kind === 'ok') {
        return 'ok';
    }
    if (plan.kind === 'forbidden') {
        return 'forbidden';
    }
    await client.sendStateEvent(roomId, 'm.room.power_levels', plan.content as Record<string, unknown>, '');
    return 'healed';
};
